import { useState } from 'react';
import { Box, Divider, Stack, Tab, Tabs } from '@mui/material';
import { CompletedCustomFieldInfo, CustomField } from '../GeneratedClient';
import { CustomFieldEditor } from '../Generic/CustomFieldEditor';
import { useDirectoryModel } from '../Model/DirectoryModel';

type CustomFieldRenderInfo = CompletedCustomFieldInfo | string;

type CustomFieldSection = {
  groupingKey?: string;
  customFields: CustomFieldRenderInfo[];
};

type FamilyMemberCustomFieldsProps = {
  familyId: string;
  personId: string;
  customFieldPolicies: CustomField[];
  completedCustomFields?: CompletedCustomFieldInfo[];
};

function isCompletedCustomField(
  customField: CustomFieldRenderInfo
): customField is CompletedCustomFieldInfo {
  return typeof customField !== 'string';
}

function customFieldName(customField: CustomFieldRenderInfo) {
  return isCompletedCustomField(customField)
    ? customField.customFieldName!
    : customField;
}

function orderCustomFieldsByPolicy(
  customFields: CustomFieldRenderInfo[],
  fieldNamesInOrder: string[]
) {
  const customFieldsByName = new Map(
    customFields.map((customField) => [
      customFieldName(customField),
      customField,
    ])
  );

  return fieldNamesInOrder.flatMap((fieldName) => {
    const customField = customFieldsByName.get(fieldName);
    return customField == null ? [] : [customField];
  });
}

function groupCustomFieldsByPolicy(
  customFields: CustomFieldRenderInfo[],
  customFieldPolicies: CustomField[]
) {
  const policiesByName = new Map(
    customFieldPolicies.map((customFieldPolicy) => [
      customFieldPolicy.name,
      customFieldPolicy,
    ])
  );

  return customFields.reduce<CustomFieldSection[]>((sections, customField) => {
    const groupingKey =
      policiesByName.get(customFieldName(customField))?.groupingKey ??
      undefined;
    if (sections.some((section) => section.groupingKey === groupingKey)) {
      return sections.map((section) =>
        section.groupingKey === groupingKey
          ? {
              ...section,
              customFields: section.customFields.concat(customField),
            }
          : section
      );
    }

    return sections.concat({ groupingKey, customFields: [customField] });
  }, []);
}

export function FamilyMemberCustomFields({
  familyId,
  personId,
  customFieldPolicies,
  completedCustomFields = [],
}: FamilyMemberCustomFieldsProps) {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const completedFieldNames = new Set(
    completedCustomFields.map((field) => field.customFieldName)
  );
  const customFields = orderCustomFieldsByPolicy(
    Array<CustomFieldRenderInfo>()
      .concat(completedCustomFields)
      .concat(
        customFieldPolicies
          .filter((field) => !completedFieldNames.has(field.name))
          .map((field) => field.name!)
      ),
    customFieldPolicies.map((field) => field.name!)
  );

  if (customFields.length === 0) {
    return null;
  }

  const customFieldSections = groupCustomFieldsByPolicy(
    customFields,
    customFieldPolicies
  );
  const selectedTab =
    selectedSectionIndex < customFieldSections.length
      ? selectedSectionIndex
      : 0;

  return (
    <Box sx={{ clear: 'both' }}>
      <Tabs
        value={selectedTab}
        onChange={(_, nextTab) => setSelectedSectionIndex(nextTab)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Custom field groups"
        sx={{
          minHeight: 28,
          mb: 0.5,
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        {customFieldSections.map((section, sectionIndex) => (
          <Tab
            key={section.groupingKey ?? `ungrouped-${sectionIndex}`}
            label={section.groupingKey ?? 'Other'}
            id={`family-member-custom-fields-tab-${personId}-${sectionIndex}`}
            aria-controls={`family-member-custom-fields-tabpanel-${personId}-${sectionIndex}`}
            sx={{
              minHeight: 28,
              px: 1,
              py: 0,
              fontSize: '0.75rem',
              textTransform: 'none',
            }}
          />
        ))}
      </Tabs>
      {customFieldSections.map((section, sectionIndex) => (
        <Box
          key={section.groupingKey ?? `ungrouped-panel-${sectionIndex}`}
          role="tabpanel"
          hidden={selectedTab !== sectionIndex}
          id={`family-member-custom-fields-tabpanel-${personId}-${sectionIndex}`}
          aria-labelledby={`family-member-custom-fields-tab-${personId}-${sectionIndex}`}
        >
          {selectedTab === sectionIndex && (
            <FamilyMemberCustomFieldList
              familyId={familyId}
              personId={personId}
              customFieldPolicies={customFieldPolicies}
              customFields={section.customFields}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

type FamilyMemberCustomFieldListProps = {
  familyId: string;
  personId: string;
  customFieldPolicies: CustomField[];
  customFields: CustomFieldRenderInfo[];
};

function FamilyMemberCustomFieldList({
  familyId,
  personId,
  customFieldPolicies,
  customFields,
}: FamilyMemberCustomFieldListProps) {
  return (
    <Stack divider={<Divider flexItem />}>
      {customFields.map((customField) => (
        <FamilyMemberCustomField
          key={customFieldName(customField)}
          familyId={familyId}
          personId={personId}
          customFieldPolicies={customFieldPolicies}
          customField={customField}
        />
      ))}
    </Stack>
  );
}

type FamilyMemberCustomFieldProps = {
  familyId: string;
  personId: string;
  customFieldPolicies: CustomField[];
  customField: CustomFieldRenderInfo;
};

function FamilyMemberCustomField({
  familyId,
  personId,
  customFieldPolicies,
  customField,
}: FamilyMemberCustomFieldProps) {
  const directoryModel = useDirectoryModel();
  const savedCustomField = isCompletedCustomField(customField)
    ? customField
    : null;
  const customFieldPolicy = customFieldPolicies.find((cf) =>
    savedCustomField
      ? cf.name === savedCustomField.customFieldName
      : cf.name === customField
  );

  if (customFieldPolicy == null) {
    return null;
  }

  return (
    <CustomFieldEditor
      customFieldPolicy={customFieldPolicy}
      completedCustomFieldInfo={savedCustomField ?? undefined}
      presentation="drawer"
      onSave={async (value) => {
        await directoryModel.updateCustomFamilyMemberField(
          familyId,
          personId,
          customFieldPolicy,
          value
        );
      }}
    />
  );
}
